import { FC } from 'react';
import { Box, DialogContent } from '@mui/material';
import { useMeasure } from 'react-use';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { QueryKeys } from '@config/config';
import { ViewNavigationBar } from '@components';
import { useCreateLogoArtwork } from '@core/features/artworks';
import { useLoadingDialog } from '@hooks';
import { useBasketStore, useBasketLimit } from '@customer/features/basket';

import { TextEditor } from './logo-artwork-editor/TextEditor';
import { TextForm } from './logo-artwork-editor/TextForm';
import { useTextEditorStore } from './logo-artwork-editor';
import { useGetSpacing } from '@ui';

type Props = {
  onClose: (confirmClose?: boolean) => void;
};

export const EditLogoStep: FC<Props> = ({ onClose }) => {
  const [dialog, handleProgress] = useLoadingDialog();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const addPosition = useBasketStore((state) => state.addPosition);
  const { maxOrderQuantity } = useBasketLimit();

  const { mutateAsync } = useCreateLogoArtwork(handleProgress);
  const { isValidString, text } = useTextEditorStore();
  const [boxRef, { height }] = useMeasure();
  const getSpacing = useGetSpacing();

  const handleCreateArtwork = async () => {
    try {
      const resData = await mutateAsync({
        logo_text: text,
      });
      queryClient.setQueryData([QueryKeys.ARTWORKS, resData.id], resData);
      if (addPosition(resData.id, maxOrderQuantity)) {
        toast.success(t('basketAddedToCart'));
      } else {
        toast.error(t('basketLimitReached', { limit: maxOrderQuantity }));
      }
      onClose(false);
    } catch (error) {
      console.error('Error creating artwork', error);
      onClose();
    }
  };
  return (
    <>
      <ViewNavigationBar
        onClose={() => onClose()}
        title="Dein Logo"
        onNext={handleCreateArtwork}
        nextDisabled={!isValidString}
        nextTitle="Weiter"
      />
      <DialogContent
        ref={boxRef}
        sx={{
          position: 'relative',
          height: '100%',
          minHeight: {
            md: 'calc(100vh - 400px)',
          },
          padding: 0,
          backgroundColor: '#ECF1FC',
        }}
      >
        <Box
          sx={{
            height: height,
            padding: getSpacing('xl'),
          }}
        >
          <TextEditor />
        </Box>
      </DialogContent>
      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            display: 'flex',
            backgroundColor: '#FFFFFF',
            paddingLeft: getSpacing('xl'),
            paddingRight: getSpacing('xl'),
            width: '100%',
            alignItems: 'center',
            height: {
              xs: 86,
              sm: 86,
              md: 86,
            },
          }}
        >
          <TextForm />
        </Box>
      </Box>

      {dialog}
    </>
  );
};
